import { useQueryClient } from "@tanstack/react-query";

import { useFetchMutation, useFetchQuery } from "common/queries/hooks";
import { noop } from "common/utils/noop";

import { useClientUserQuery } from "client/accounts/hooks";

import {
  DeliveryMethod,
  DosageReminder,
  DosageReminderCreate,
  DosageReminderPatientItem,
  DosageReminderPatientItemSearch,
  DosageRemindersSearch,
  DosageReminderUpdate,
} from "../types";
import { DELIVERY_METHOD_OPTIONS } from "../constants";
import { keys, urls } from "./constants";

export const useDosageRemindersQuery = (search: DosageRemindersSearch = {}) => {
  return useFetchQuery<DosageReminder[], DosageRemindersSearch>(keys.dosageReminderList(search), {
    fetch: { url: urls.dosageReminders(), body: search },
  });
};

export const useDosageReminderQuery = (id: number) => {
  return useFetchQuery<DosageReminder>(keys.dosageReminderDetail(id), {
    fetch: { url: urls.dosageReminder(id) },
  });
};

export const useDosageReminderRecentItemsQuery = () => {
  return useFetchQuery<DosageReminderPatientItem[]>(keys.dosageReminderRecentItems(), {
    fetch: { url: urls.dosageReminderRecentItems() },
  });
};

export const useDosageReminderPatientItemQuery = (search: DosageReminderPatientItemSearch) => {
  return useFetchQuery<DosageReminderPatientItem, DosageReminderPatientItemSearch>(
    keys.dosageReminderPatientItem(search),
    { fetch: { url: urls.dosageReminderPatientItem(), body: search } },
  );
};

export const useCreateDosageReminderMutation = ({
  onSuccess = noop,
}: { onSuccess?: () => void } = {}) => {
  const queryClient = useQueryClient();
  return useFetchMutation<DosageReminderCreate, Pick<DosageReminder, "id">>({
    fetch: (body) => ({ method: "POST", url: urls.dosageReminders(), body }),
    config: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.dosageReminder() });
        onSuccess();
      },
    },
  });
};

export const useUpdateDosageReminderMutation = (
  id: number,
  { onSuccess = noop }: { onSuccess?: () => void } = {},
) => {
  const queryClient = useQueryClient();
  return useFetchMutation<DosageReminderUpdate, DosageReminder>({
    fetch: (body) => ({ method: "PATCH", url: urls.dosageReminder(id), body }),
    config: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.dosageReminder() });
        onSuccess();
      },
    },
  });
};

export const useDeliveryMethodSelection = (value: Record<DeliveryMethod, boolean>) => {
  const { data: clientUser, isLoading } = useClientUserQuery();

  const destinations: Record<DeliveryMethod, string | undefined> = {
    text: clientUser?.primary_contact_number?.number_display,
    email: clientUser?.primary_contact_email?.email,
  };
  const availableMethodCount = DELIVERY_METHOD_OPTIONS.filter(
    (option) => destinations[option.value],
  ).length;

  // A method with no destination can't be selected, and a lone valid method can't be deselected
  const isSelected = (method: DeliveryMethod) =>
    !!destinations[method] && (availableMethodCount === 1 || value[method]);

  return {
    clientUser,
    destinations,
    availableMethodCount,
    isSelected,
    isLoading,
  };
};
